import { useEffect, useId, useMemo, useState } from 'react';
import { Check, ChevronDown, Loader2, PaintBucket, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { GOOGLE_FONTS, hslToHex } from '@/lib/googleFonts';
import { hexToHsl } from '@/lib/colorUtils';
import { resolveThemeTokensFromSeed } from '@/lib/themeResolver';
import type {
  NormalizedTheme,
  NormalizedThemePreset,
  ThemeFonts,
  ThemeSeed,
  ThemeSelection,
  ThemeTokens,
} from '@/types/theme';

type SemanticThemeEditorProps = {
  themeSelection: ThemeSelection | null;
  normalizedTheme: NormalizedTheme;
  presets: NormalizedThemePreset[];
  loading: boolean;
  saving: boolean;
  onSavePreset: (themeId: string) => Promise<boolean>;
  onSaveCustom: (seed: ThemeSeed, fonts?: ThemeFonts) => Promise<boolean>;
  onPreviewTokensChange?: (tokens: ThemeTokens) => void;
};

const MAIN_SEED_KEYS: Array<{ key: keyof ThemeSeed; label: string; description: string }> = [
  { key: 'brand', label: 'Principal', description: 'Botones y acciones principales' },
  { key: 'accent', label: 'Acento', description: 'Chips, destacados y detalles' },
  { key: 'background', label: 'Fondo', description: 'Fondo general del sitio' },
  { key: 'surface', label: 'Contenedores', description: 'Tarjetas, modales y superficies' },
];

const ADVANCED_SEED_KEYS: Array<{ key: keyof ThemeSeed; label: string; description: string }> = [
  { key: 'text', label: 'Texto', description: 'Texto principal del sitio' },
  { key: 'border', label: 'Bordes', description: 'Bordes, inputs y separadores' },
];

function buildDraftSignature(presetId: string | null, seed: ThemeSeed, fonts: ThemeFonts) {
  return JSON.stringify({
    presetId,
    seed,
    fonts,
  });
}

function buildPresetSwatches(seed: ThemeSeed) {
  const orderedKeys: Array<keyof ThemeSeed> = ['brand', 'accent', 'background', 'surface', 'text', 'border'];
  const uniqueColors: string[] = [];

  orderedKeys.forEach((key) => {
    const nextColor = seed[key];
    if (!nextColor || uniqueColors.includes(nextColor)) {
      return;
    }

    uniqueColors.push(nextColor);
  });

  return uniqueColors.slice(0, 4);
}

function extractPrimaryFontName(family?: string) {
  return family?.split(',')[0]?.trim().replace(/['"]/g, '') || '';
}

function fontNameForFamily(family?: string) {
  if (!family) {
    return '';
  }

  const primaryFamily = extractPrimaryFontName(family);
  return GOOGLE_FONTS.find((font) => font.name === primaryFamily)?.name || primaryFamily;
}

function getSelectLabelStyle(family?: string) {
  if (extractPrimaryFontName(family) === 'Borel') {
    return {
      lineHeight: '1.35',
      paddingTop: '1rem',
      paddingBottom: '0.02rem',
    };
  }

  return {
    lineHeight: '1.2',
  };
}

function ColorSeedInput({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (nextValue: string) => void;
}) {
  const inputId = useId();
  const hexValue = hslToHex(value);

  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <label htmlFor={inputId} className="relative block h-16 w-16 cursor-pointer sm:h-20 sm:w-20">
        <div
          className="h-full w-full rounded-full border-[3px] border-white shadow-[0_12px_28px_rgba(15,23,42,0.14)] ring-1 ring-slate-200"
          style={{ backgroundColor: `hsl(${value})` }}
        />
      </label>
      <input
        id={inputId}
        type="color"
        value={hexValue}
        onChange={(event) => onChange(hexToHsl(event.target.value))}
        className="sr-only"
        aria-label={label}
      />
      <div>
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{hexValue.toUpperCase()}</div>
        <div className="mt-1 max-w-[124px] text-[11px] leading-4 text-slate-400">{description}</div>
      </div>
    </div>
  );
}

const SemanticThemeEditor = ({
  themeSelection,
  normalizedTheme,
  presets,
  loading,
  saving,
  onSavePreset,
  onSaveCustom,
  onPreviewTokensChange,
}: SemanticThemeEditorProps) => {
  const [draftSeed, setDraftSeed] = useState<ThemeSeed>(normalizedTheme.seed);
  const [draftFonts, setDraftFonts] = useState<ThemeFonts>(normalizedTheme.fonts);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(themeSelection?.type === 'preset' ? themeSelection.theme_id : null);
  const [showAdvanced, setShowAdvanced] = useState(themeSelection?.type === 'custom');

  const baselineSignature = useMemo(() => {
    const presetId = themeSelection?.type === 'preset' ? themeSelection.theme_id : null;
    return buildDraftSignature(presetId, normalizedTheme.seed, normalizedTheme.fonts);
  }, [normalizedTheme.fonts, normalizedTheme.seed, themeSelection]);

  useEffect(() => {
    setDraftSeed(normalizedTheme.seed);
    setDraftFonts(normalizedTheme.fonts);
    setSelectedPresetId(themeSelection?.type === 'preset' ? themeSelection.theme_id : null);
    setShowAdvanced(themeSelection?.type === 'custom');
  }, [normalizedTheme.fonts, normalizedTheme.seed, themeSelection]);

  const selectedPreset = useMemo(
    () => presets.find((preset) => preset.themeId === selectedPresetId) || null,
    [presets, selectedPresetId],
  );

  const draftSignature = useMemo(
    () => buildDraftSignature(selectedPresetId, draftSeed, draftFonts),
    [draftFonts, draftSeed, selectedPresetId],
  );

  const hasChanges = draftSignature !== baselineSignature;
  const usesPresetPreview =
    !!selectedPreset &&
    buildDraftSignature(selectedPreset.themeId, selectedPreset.seed, selectedPreset.fonts) === draftSignature;

  const previewTokens = useMemo(() => {
    if (!hasChanges) {
      return normalizedTheme.tokens;
    }

    if (usesPresetPreview && selectedPreset) {
      return selectedPreset.tokens;
    }

    return resolveThemeTokensFromSeed(draftSeed, draftFonts);
  }, [draftFonts, draftSeed, hasChanges, normalizedTheme.tokens, selectedPreset, usesPresetPreview]);

  useEffect(() => {
    onPreviewTokensChange?.(previewTokens);
  }, [onPreviewTokensChange, previewTokens]);

  const headingFontName = fontNameForFamily(draftFonts.headings);
  const generalFontName = fontNameForFamily(draftFonts.general);
  const headingFontLabelStyle = getSelectLabelStyle(draftFonts.headings);
  const generalFontLabelStyle = getSelectLabelStyle(draftFonts.general);

  const availableHeadingFonts = useMemo(() => {
    if (!headingFontName || GOOGLE_FONTS.some((font) => font.name === headingFontName)) {
      return GOOGLE_FONTS;
    }

    return [
      {
        name: headingFontName,
        family: draftFonts.headings || headingFontName,
        category: 'display' as const,
      },
      ...GOOGLE_FONTS,
    ];
  }, [draftFonts.headings, headingFontName]);

  const availableGeneralFonts = useMemo(() => {
    if (!generalFontName || GOOGLE_FONTS.some((font) => font.name === generalFontName)) {
      return GOOGLE_FONTS;
    }

    return [
      {
        name: generalFontName,
        family: draftFonts.general || generalFontName,
        category: 'sans-serif' as const,
      },
      ...GOOGLE_FONTS,
    ];
  }, [draftFonts.general, generalFontName]);

  const applyPresetDraft = (preset: NormalizedThemePreset) => {
    setDraftSeed(preset.seed);
    setDraftFonts(preset.fonts);
    setSelectedPresetId(preset.themeId);
  };

  const updateSeed = (key: keyof ThemeSeed, value: string) => {
    setDraftSeed((current) => ({ ...current, [key]: value }));
    setSelectedPresetId(null);
  };

  const updateFont = (key: keyof ThemeFonts, fontName: string) => {
    const font = GOOGLE_FONTS.find((item) => item.name === fontName);
    if (!font) return;

    setDraftFonts((current) => ({
      ...current,
      [key]: font.family,
    }));
    setSelectedPresetId(null);
  };

  const resetDraft = () => {
    setDraftSeed(normalizedTheme.seed);
    setDraftFonts(normalizedTheme.fonts);
    setSelectedPresetId(themeSelection?.type === 'preset' ? themeSelection.theme_id : null);
    setShowAdvanced(themeSelection?.type === 'custom');
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    if (selectedPresetId && usesPresetPreview) {
      await onSavePreset(selectedPresetId);
      return;
    }

    await onSaveCustom(draftSeed, draftFonts);
  };

  return (
    <div className="rounded-[28px] border border-indigo-100/80 bg-white/90 p-6 shadow-[0_18px_48px_rgba(110,86,207,0.12)] backdrop-blur">


      <div className="space-y-8">
        <section>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Temas predefinidos</h2>
              <p className="text-sm text-slate-500">Elige un set de colores y fuentes predefinido, o usalo como punto de partida:</p>
            </div>
            <div className="flex items-center gap-3">
              {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              {hasChanges ? (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="hover:bg-violet-300 hover:text-black"
                    onClick={resetDraft}
                    disabled={saving}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Restaurar
                  </Button>
                  <Button
                    type="button"
                    className="bg-violet-300 text-black hover:text-black"
                    onClick={() => { void handleSave(); }}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Guardar
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {presets.map((preset) => {
              const isSelected = selectedPresetId === preset.themeId && usesPresetPreview;
              const swatches = buildPresetSwatches(preset.seed);

              return (
                <button
                  key={preset.themeId}
                  type="button"
                  onClick={() => applyPresetDraft(preset)}
                  className={`group cursor-pointer rounded-2xl border px-4 py-3 text-left transition-all duration-200 ${isSelected
                    ? 'border-violet-400 bg-violet-50/80 shadow-[0_10px_24px_rgba(124,77,255,0.12)]'
                    : 'border-slate-200 bg-white hover:border-violet-200 hover:shadow-[0_10px_24px_rgba(15,23,42,0.06)]'
                    }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-slate-900">{preset.name}</div>
                    {isSelected && (
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-500 text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center -space-x-1.5">
                    {swatches.map((color, index) => (
                      <span
                        key={`${preset.themeId}-${color}-${index}`}
                        className="h-5 w-5 rounded-full border-2 border-white shadow-[0_2px_8px_rgba(15,23,42,0.12)]"
                        style={{ backgroundColor: `hsl(${color})` }}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <button
            type="button"
            onClick={() => setShowAdvanced((current) => !current)}
            className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-violet-300 hover:text-violet-600"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            Personalizar colores
          </button>

          {showAdvanced && (
            <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50/80 p-5">
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                {MAIN_SEED_KEYS.map(({ key, label, description }) => (
                  <ColorSeedInput
                    key={key}
                    label={label}
                    description={description}
                    value={draftSeed[key]}
                    onChange={(nextValue) => updateSeed(key, nextValue)}
                  />
                ))}
              </div>

              <div className="mt-6 grid max-w-[360px] grid-cols-2 gap-6">
                {ADVANCED_SEED_KEYS.map(({ key, label, description }) => (
                  <ColorSeedInput
                    key={key}
                    label={label}
                    description={description}
                    value={draftSeed[key]}
                    onChange={(nextValue) => updateSeed(key, nextValue)}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Tipografía</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Títulos</div>
              <div className="mt-1 text-sm text-slate-500">Elegí una fuente para títulos</div>
              <Select value={headingFontName} onValueChange={(value) => updateFont('headings', value)}>
                <SelectTrigger
                  className="mt-4 h-11 text-base"
                  style={{
                    fontFamily: draftFonts.headings,
                  }}
                >
                  <span
                    className="block flex-1 truncate overflow-visible text-left"
                    style={headingFontLabelStyle}
                  >
                    {headingFontName || 'Seleccioná una fuente'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {availableHeadingFonts.map((font) => (
                    <SelectItem
                      key={`heading-${font.name}`}
                      value={font.name}
                      style={{
                        fontFamily: font.family,
                      }}
                    >
                      <span
                        className="block overflow-visible"
                        style={getSelectLabelStyle(font.family)}
                      >
                        {font.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-[22px] border border-slate-200 bg-white px-4 py-4 shadow-sm">
              <div className="text-sm font-semibold text-slate-900">Texto general</div>
              <div className="mt-1 text-sm text-slate-500">Elegí una fuente para texto</div>
              <Select value={generalFontName} onValueChange={(value) => updateFont('general', value)}>
                <SelectTrigger
                  className="mt-4 h-11 text-base"
                  style={{
                    fontFamily: draftFonts.general,
                  }}
                >
                  <span
                    className="block flex-1 truncate overflow-visible text-left"
                    style={generalFontLabelStyle}
                  >
                    {generalFontName || 'Seleccioná una fuente'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {availableGeneralFonts.map((font) => (
                    <SelectItem
                      key={`general-${font.name}`}
                      value={font.name}
                      style={{
                        fontFamily: font.family,
                      }}
                    >
                      <span
                        className="block overflow-visible"
                        style={getSelectLabelStyle(font.family)}
                      >
                        {font.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SemanticThemeEditor;
